import {
  isObjectEmptyDeep,
  getRegionAbbreviation,
  getCountryNameByCode,
} from 'src/services/util.service'
import { refactorQaSection } from 'src/services/helpers/product.helper'
import {
  ASSESSMENT_STATUS,
  initialCompanyAiAssessmentTemplate,
} from 'src/services/consts/company.const'
import {
  COUNTRIES_WITH_REGIONS_CODE_MAP,
  COUNTRIES_WITH_REGIONS,
} from 'src/services/consts/countries.const'
import { refactorCompanyFiles } from 'src/services/helpers/companyFile.helper'

// DPF framework extraction
const FRAMEWORK_KEYS = ['eu', 'uk', 'swiss']
const FRAMEWORK_LABELS = {
  eu: 'EU-U.S.',
  uk: 'UK Extension to EU-U.S.',
  swiss: 'Swiss-U.S.',
}

function extractFrameworks(dpfInfo) {
  return FRAMEWORK_KEYS.filter((key) => dpfInfo[key] && typeof dpfInfo[key] === 'object').map(
    (key) => ({
      id: key,
      label: FRAMEWORK_LABELS[key],
      status: dpfInfo[key].status || null,
      hrData: dpfInfo[key].hr_data ?? false,
      nonHrData: dpfInfo[key].non_hr_data ?? false,
      installDate: dpfInfo[key].install_date || null,
      usageEndDate: dpfInfo[key].usage_end_date || null,
      inactiveStartDate: dpfInfo[key].inactive_start_date || null,
    }),
  )
}

// Address utilities
export function getInitialAddressOrder(addresses, mainAddressId) {
  if (!addresses) return []

  // Create address objects with metadata for sorting
  const addressObjects = Object.entries(addresses)
    .filter(([, address]) => !isObjectEmptyDeep(address))
    .map(([addressId, address]) => ({
      id: addressId,
      address,
      isMain: addressId === mainAddressId,
    }))

  // Sort: main first, then titled, then rest
  const sorted = addressObjects.sort((a, b) => {
    // Main address first
    if (a.isMain) return -1
    if (b.isMain) return 1

    // Among non-main addresses, sort by title (empty strings last)
    const aTitle = a.address.address_title?.trim() || ''
    const bTitle = b.address.address_title?.trim() || ''

    if (aTitle && !bTitle) return -1
    if (!aTitle && bTitle) return 1
    return aTitle.localeCompare(bTitle)
  })

  return sorted.map((obj) => obj.id)
}

export function getFormattedAddresses(addresses, mainAddressId, order) {
  if (!addresses || !order || order.length === 0) return []

  // Create address objects lookup
  const addressLookup = Object.entries(addresses)
    .filter(([, address]) => !isObjectEmptyDeep(address))
    .reduce((acc, [addressId, address]) => {
      acc[addressId] = address
      return acc
    }, {})

  // Format addresses according to order (skip deleted ones)
  return order
    .filter((id) => addressLookup[id]) // Skip deleted addresses
    .map((id, index) => {
      const address = addressLookup[id]
      const isMain = id === mainAddressId

      // Generate label
      let label
      if (address.address_title?.trim()) {
        label = address.address_title
      } else {
        label = `Address ${index + 1}`
      }

      return {
        id,
        label,
        value: formatAddress(address),
        isMain,
        address,
      }
    })
}

export function formatAddress(address) {
  if (!address) return ''

  const parts = []
  const countryCode = address.regionCode
  const countryName = formatCountry(countryCode)

  // Add address lines (common for all countries)
  if (address.addressLines && address.addressLines.length > 0) {
    parts.push(...address.addressLines)
  }

  // Format city, state/province, and postal code based on country
  const locationParts = []
  if (address.locality) {
    locationParts.push(address.locality)
  }
  if (address.administrativeArea) {
    // For US, UK, and Germany, use region abbreviation to avoid redundancy
    const mappedCountryCode = COUNTRIES_WITH_REGIONS_CODE_MAP[countryCode] || countryCode

    const regionName = COUNTRIES_WITH_REGIONS.includes(countryCode)
      ? getRegionAbbreviation(address.administrativeArea, mappedCountryCode)
      : address.administrativeArea
    locationParts.push(regionName)
  }
  if (address.postalCode) {
    locationParts.push(address.postalCode)
  }

  if (locationParts.length > 0) {
    // UK uses space between city and postcode, others use comma
    const separator = countryCode === 'GB' ? ' ' : ', '
    parts.push(locationParts.join(separator))
  }

  // Country on separate line (common for all countries)
  if (countryName) {
    parts.push(countryName)
  }

  return parts.join('<br>')
}

export function formatCountry(countryCode) {
  if (!countryCode) return ''
  return getCountryNameByCode(countryCode) || countryCode
}

// Data transformation functions
export function refactorCompanyData(rawData) {
  if (!rawData) return null

  return {
    id: rawData.companyId,
    info: refactorCompanySection('info', rawData),
    parent: refactorCompanySection('parent', rawData),
    contact: refactorCompanySection('contact', rawData),
    dpf: refactorCompanySection('dpf', rawData),
    aiAssessment: refactorCompanySection('aiAssessment', rawData),
    products: refactorCompanySection('products', rawData),
    files: refactorCompanyFiles(rawData.files),
  }
}

export function refactorCompanySection(section, data) {
  const {
    dpf_info,
    parent_company_info,
    contact_info,
    company_ai_assessment_info,
    company_ai_assessment_qa,
    products,
    company_info,
  } = data

  switch (section) {
    case 'info':
      if (!company_info) return null

      return {
        id: company_info.company_id,
        active: company_info.active,
        name: company_info.company_name,
        legalName: company_info.company_legal_name,
        dateAdded: company_info.date_added,
        isDpfFound: company_info.dpf_found,
        parentId: company_info.parent_id,
        providesAiServices: company_info.provides_ai_services ?? null,
        website: Array.isArray(company_info.website)
          ? company_info.website[0] || ''
          : company_info.website || '',
        assessmentTypeToUse: company_info.ai_assessment_to_use,
        sourceOfData: company_info.source,
        companyAssessmentStatus: company_info.company_ai_assessment_processing_status,
        companyInfoStatus: company_info.company_general_info_processing_status,
        companyProductsStatus: company_info.list_of_products_processing_status,
        attachmentsFetchProcessingStatus: company_info.attachments_fetch_processing_status,
        attachmentsFetchPending: company_info.attachments_fetch_pending ?? [],
        isPublicCompany: company_info.is_public_company || false,
        stockExchangeName: company_info.company_stock_exchange_name || '',
        tickerSymbol: company_info.company_ticker_symbol || '',
        numberOfEmployees: company_info.company_number_of_employees || '',
        generalInfoCreatedOn: company_info.general_info_created_on || '',
      }

    case 'parent':
      if (!parent_company_info) return null

      return {
        id: parent_company_info.company_id,
        active: parent_company_info.active,
        name: parent_company_info.company_name,
        legalName: parent_company_info.company_legal_name,
        dateAdded: parent_company_info.date_added,
        isDpfFound: parent_company_info.dpf_found,
        parentId: parent_company_info.parent_id,
        providesAiServices: parent_company_info.provides_ai_services,
      }

    case 'contact':
      if (!contact_info) return null

      // Process addresses
      const addresses = {}
      if (contact_info.addresses) {
        Object.keys(contact_info.addresses).forEach((addressId) => {
          const address = contact_info.addresses[addressId]
          if (address.postalAddress) {
            addresses[addressId] = {
              address_title: address.address_title || '',
              addressLines: address.postalAddress.addressLines || [],
              administrativeArea: address.postalAddress.administrativeArea || '',
              locality: address.postalAddress.locality || '',
              postalCode: address.postalAddress.postalCode || '',
              regionCode: address.postalAddress.regionCode || '',
            }
          }
        })
      }
      // Test build

      return {
        name: contact_info.name || '',
        title: contact_info.title || '',
        email: contact_info.email || '',
        phoneNumber: contact_info.phone_number || '',
        breachNotificationEmail: contact_info.breach_notification_email || '',
        addresses: addresses,
        mainAddressId: contact_info.main_address_id || null,
      }

    case 'dpf':
      if (!dpf_info) return null

      return {
        dpfId: company_info?.dpf_id || null,
        legalName: dpf_info.legal_name || null,
        publicDisplayName: dpf_info.public_display_name || null,
        street: dpf_info.street || null,
        city: dpf_info.city || null,
        state: dpf_info.state || null,
        postalCode: dpf_info.postal_code || null,
        industries: dpf_info.industries || null,
        privacyPolicies: dpf_info.privacy_policies || null,
        publicLink: dpf_info.public_link || null,
        coveredEntities: dpf_info.covered_entities || null,
        frameworks: extractFrameworks(dpf_info),
      }

    case 'aiAssessment':
      if (!company_ai_assessment_info && !company_ai_assessment_qa) {
        // Return the template when no assessment data is available
        return { ...initialCompanyAiAssessmentTemplate }
      }

      return {
        // Basic assessment info
        assessmentTypeToUse: company_ai_assessment_info?.ai_assessment_to_use || '',
        createdByAi: company_ai_assessment_info?.created_by_ai || false,
        createdOn: company_ai_assessment_info?.created_on || '',

        // Refactored Q&A sections with proper camelCase naming
        sections: refactorQaSection(company_ai_assessment_qa),
      }

    case 'products':
      if (!products || !Array.isArray(products) || products.length === 0) return null

      return products.map((product) => ({
        id: product.product_id,
        name: product.product_name || '',
        usesAi: product.product_uses_ai || false,
        active: product.active ?? false,
      }))

    default:
      return null
  }
}

function mergeCompanySection(prevSection, updatedSection) {
  if (updatedSection == null) return prevSection
  if (prevSection == null) return updatedSection

  if (Array.isArray(prevSection) || Array.isArray(updatedSection)) {
    return Array.isArray(updatedSection) ? updatedSection : prevSection
  }

  if (typeof prevSection === 'object' && typeof updatedSection === 'object') {
    return { ...prevSection, ...updatedSection }
  }

  return updatedSection
}

// Cache update logic (for Tanstack Query mutations)
export function buildUpdatedCompany(prevCompany, updatedSection, payload) {
  let updatedCompany = null

  // Handle AI Assessment section updates
  if (payload.sectionToUpdate === 'aiAssessment') {
    updatedCompany = {
      ...prevCompany,
      aiAssessment: {
        ...prevCompany.aiAssessment,
        sections: updatedSection,
      },
    }
  } else {
    const sectionKey = payload.sectionToUpdate
    const prevSection = prevCompany[sectionKey]
    updatedCompany = {
      ...prevCompany,
      [sectionKey]: mergeCompanySection(prevSection, updatedSection),
    }
  }

  // Special handling for parent section
  if (payload.sectionToUpdate === 'parent') {
    updatedCompany.info = {
      ...updatedCompany.info,
      parentId: payload.dataToUpdate.parent_id,
    }
    if (!updatedCompany.info.parentId) {
      updatedCompany.parent = null
    }
  }

  return updatedCompany
}

//Get country code (2-letter) from a company's main address regionCode
export function getCountryFromMainAddress(company) {
  if (!company?.contact?.addresses || !company.contact.mainAddressId) {
    return null
  }
  const mainAddress = company.contact.addresses[company.contact.mainAddressId]
  return mainAddress?.regionCode || null
}

// AI task status helpers
export function getAiTaskState(companyInfo, additionalStatus = null) {
  if (!companyInfo) return { isActive: false, variant: 'assessment' }

  const isGatheringInfo = companyInfo.companyInfoStatus === ASSESSMENT_STATUS.IN_PROGRESS
  const isOtherTaskActive = additionalStatus
    ? companyInfo[additionalStatus] === ASSESSMENT_STATUS.IN_PROGRESS
    : false

  return {
    isActive: isGatheringInfo || isOtherTaskActive,
    variant: isGatheringInfo ? 'info' : 'assessment',
  }
}
